import AbstractFormControl from './AbstractFormControl';
import FormValidity from './FormValidity';

class FormArray<T> extends AbstractFormControl<T[]> {
  public readonly controls: AbstractFormControl<T>[];

  constructor(controls: AbstractFormControl<T>[]) {
    const values = controls.map((control) => control.value);
    super(values);
    this.controls = controls;
  }

  public get value(): T[] {
    return this.controls.map((control) => control.value);
  }

  public get pristine(): boolean {
    return this.controls.every((control) => control.pristine);
  }

  public get dirty(): boolean {
    return this.controls.some((control) => control.dirty);
  }

  public get valid(): boolean {
    return this.controls.every((control) => control.valid);
  }

  public get invalid(): boolean {
    return this.controls.some((control) => control.invalid);
  }

  public get pending(): boolean {
    return this.controls.some((control) => control.pending);
  }

  public update(values: T[]) {
    this.controls.forEach((control, i) => {
      control.update(values?.[i]);
    });
  }

  public insert(control: AbstractFormControl<T>, index: number) {
    this.controls.splice(index, 0, control);
    this.runValidators();
  }

  public remove(index: number) {
    this.controls.splice(index, 1);
    this.runValidators();
  }

  public push(control: AbstractFormControl<T>) {
    this.controls.push(control);
    this.runValidators();
  }

  public async runValidators(): Promise<FormValidity> {
    this.validity = FormValidity.Pending;
    await Promise.allSettled(this.controls.map((control) => control.runValidators()));
    this.validity = this.controls.some((control) => control.invalid) ? FormValidity.Invalid : FormValidity.Valid;
    return this.validity;
  }

  public reset(withInitialValue?: boolean): void {
    this.controls.forEach((control) => control.reset(withInitialValue));
  }

  public markAsPristine(): void {
    for (const control of this.controls) {
      control.markAsPristine();
    }
  }

  public markAsDirty(): void {
    for (const control of this.controls) {
      control.markAsDirty();
    }
  }
}

export default FormArray;
